<template>
	<div class="min-h-[100dvh]">
		<div
			v-if="isLoading"
			class="absolute inset-0 z-50 bg-gray-100 bg-opacity-60 flex items-center justify-center h-auto"
		>
			<div
				class="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
				role="status"
			></div>
		</div>
		<div
			v-if="basket"
			class="main-wrapper grid lg:grid-cols-2 gap-4 lg:gap-x-[5%] mt-5 mb-10 lg:mt-10"
		>
			<div>
				<h1
					class="mb-0 flex items-center gap-2 text-[28px] font-bold uppercase"
				>
					{{ $t("Checkout") }}
				</h1>
				<div class="flex flex-col gap-4">
					<SharedBasket :basket="basket" :order-id="orderId" class="!mt-0" />
				</div>
			</div>
			<div class="grid grid-cols-1 gap-4">
				<div>
					<h2 class="text-[22px] text-gray-700 font-bold uppercase">
						{{ $t("Shipping") }}
					</h2>
					<component
						:is="ShippingComponent"
						v-if="basket && orderId && order && !isLoading"
						:basket="basket"
						:order-id="orderId"
						:order="order"
						:update="update"
						@delivery-updated="onDeliveryUpdated"
					/>
				</div>
				<!-- [NF-385] Self-gates on public.checkoutComment.enabled; renders
				nothing otherwise. Base component, ported here because this fork of
				Checkout.vue would not otherwise pick up the base edit. -->
				<CheckoutComment class="w-full" />
				<div>
					<h2
						v-if="basket && orderId"
						class="text-[22px] text-gray-700 font-bold uppercase"
					>
						{{ $t("Payment") }}
					</h2>
					<component
						:is="PaymentComponent"
						v-if="basket && orderId && order && !isLoading && commentSatisfied"
						:basket="basket"
						:order-id="orderId"
						:order="order"
						:update="update"
					/>
					<!-- [NF-385] Explain the empty payment step when a required comment
					gates it. -->
					<p
						v-else-if="
							basket && orderId && order && !isLoading && !commentSatisfied
						"
						class="text-sm text-red-600"
					>
						{{ $t("checkout.comment.required_to_pay") }}
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";
import type { Order } from "@nwt/norce-pwa/dist/runtime/types/api/checkout/orders/orders";
import SharedBasket from "../Shared/SharedBasket/SharedBasket.vue";
import { useIngridSuspend } from "../../composables/useIngridSuspend";

const { withIngridSuspended } = useIngridSuspend();

const config = useRuntimeConfig();
const { locale } = useI18n();
const _locales = (config.public.norce.locales ??
	[]) as import("@nwt/norce-pwa/dist/runtime/types/generic").Locale[];
const adapterComponentName =
	_locales.find((l) => l.code === locale.value)?.paymentAdapter ??
	config.public.norce.paymentAdapter;
const infoTypes = config.public.norce.shippingInfoTypeIds as Record<
	string,
	number
>;
const PaymentComponent = defineAsyncComponent(
	() => import(`./PaymentAdapter/${adapterComponentName}.vue`)
);

const shippingAdapterComponentName = config.public.norce.shippingAdapter;
const ShippingComponent = defineAsyncComponent(
	() => import(`./ShippingAdapter/${shippingAdapterComponentName}.vue`)
);

const cartStore = useCartStore();
const { basket } = storeToRefs(cartStore);
// [NF-385] false until a required comment is saved; always true otherwise.
const { satisfied: commentSatisfied } = useCheckoutComment();

const {
	norceAdapter,
	norceOrders,
	paymentAdapter,
	updateBasket,
	updateBasketPaymentMethod
} = useNorcePwa();
const norceStore = useNorceStore();
const isLoading = ref(true);
const { beginCheckout } = useGtmEvents();
const { checkout: dotdigitalCheckout } = useDotdigital();

const NorceCheckoutV2PaymentMethodId = 239;
const orderId = ref("");
const order = ref<Order | null>(null);
const update = ref("");
const isDeliveryRefreshing = ref(false);
let pendingDeliveryUpdate = false;
let lastCultureCode = norceStore.storeContext.cultureCode;
let lastBasketId = basket.value?.Id;

const buildUpdateToken = (reason: "cart" | "delivery") =>
	`${reason}:${Date.now()}`;

const getCurrentPriceListSeed = () => {
	// Worked out from the sales area, like every other price request — never
	// from something the browser kept, which is how another market's lists came
	// to price this order.
	if (!norceStore.priceListSeed) return undefined;
	const ids = norceStore.priceListSeed
		.split(",")
		.map((v) => Number(v.trim()))
		.filter((v) => Number.isFinite(v) && v > 0);
	return ids.length ? ids : undefined;
};

const emitCheckoutUpdate = async (reason: "cart" | "delivery") => {
	if (!orderId.value || !basket.value?.Id) return;

	const priceListSeed = getCurrentPriceListSeed();

	await withIngridSuspended(async () => {
		try {
			await norceAdapter.updateOrder({
				orderId: orderId.value.toString(),
				localeCode: locale.value,
				...(priceListSeed ? { priceListSeed } : {})
			});
			const currentOrder = await norceOrders.getOrderById({
				orderId: orderId.value.toString(),
				localeCode: locale.value
			});
			const klarnaPayment = currentOrder?.payments?.find(
				(payment) =>
					payment.adapterId === "klarna_checkout_adapter" && payment.id
			);

			if (klarnaPayment?.id) {
				await paymentAdapter.updatePayment({
					orderId: orderId.value.toString(),
					paymentId: klarnaPayment.id,
					flag: PaymentAdapters.KLARNA,
					localeCode: locale.value
				});
			}
		} catch (err: any) {
			const errorStatusMessage = err?.statusMessage || err?.data?.statusMessage;

			if (
				errorStatusMessage === "klarna-order-not-found" ||
				errorStatusMessage === "order-bad-request"
			) {
				isLoading.value = true;
				await norceOrders
					.deleteOrder({
						orderId: orderId.value.toString(),
						localeCode: locale.value
					})
					.catch(() => {});
				const orderBody = createOrderBody();
				const { id } = await norceAdapter.createOrder(orderBody);
				orderId.value = id;
				await updateBasket(norceStore.storeContext, {
					Id: basket.value.Id,
					OrderReference: id
				});
				await loadOrder(id);
				isLoading.value = false;
				return;
			} else {
				console.error(
					"[Checkout] Klarna pre-check before adapter refresh failed:",
					err
				);
			}
		}
		update.value = buildUpdateToken(reason);
	});
};

// Update shipping info in basket after receiving updated shipping details from the shipping adapter
// Needed for ERP when order is received on ERP
const updateShippingInfo = async () => {
	if (!order.value?.shippings?.length || !basket.value?.Id) {
		console.warn(
			"[Checkout] updateShippingInfo skipped — shippings:",
			order.value?.shippings,
			"basketId:",
			basket.value?.Id
		);
		return;
	}
	const shipping =
		order.value.shippings.find(
			(s: any) => s.adapterId === "ingrid_adapter" && s.state !== "removed"
		) || order.value.shippings[0];
	if (!shipping) return;
	const haulerIdMap = useRuntimeConfig().public.norce.haulerIdMap as Record<
		string,
		number
	>;
	const d = shipping.deliveryDetails as any;
	const raw = shipping.attributes?.shippingData;
	let shippingData: any = {};
	try {
		if (raw) {
			if (typeof raw !== "string") {
				shippingData = raw;
			} else {
				const cleaned = raw.replace(/\\"/g, '"').replace(/\\n/g, "");
				shippingData = JSON.parse(cleaned);
			}
		}
	} catch {
		console.warn("[Checkout] failed to parse shippingData:", raw);
	}
	const shippingMeta = shippingData?.delivery_groups?.[0]?.shipping?.meta;
	const locationMeta =
		shippingData?.delivery_groups?.[0]?.addresses?.location?.meta;

	const extendedShippingMeta =
		shippingMeta || locationMeta || d?.pickupLocation?.reference
			? {
					...(shippingMeta || {}),
					...(locationMeta
						? { "isb.sort_code": locationMeta["isb.sort_code"] }
						: {}),
					...(d?.class === "pickup" && d?.pickupLocation?.reference
						? { pickupPointId: d.pickupLocation.reference }
						: {})
				}
			: undefined;
	const shippingInfo = [
		{
			TypeId: infoTypes.DeliveryMethodName,
			Code: "DeliveryMethodName",
			Value: d?.product?.name || shipping.name
		},
		{
			TypeId: infoTypes.ExternalDeliveryMethodId,
			Code: "ExternalDeliveryMethodId",
			Value: d?.carrierProductId
		},
		{
			TypeId: infoTypes.ExternalDeliveryCarrierName,
			Code: "ExternalDeliveryCarrierName",
			Value: d?.carrier
		},
		{
			TypeId: infoTypes.ExternalDeliveryMethodName,
			Code: "ExternalDeliveryMethodName",
			Value: d?.product?.name
		},
		{
			TypeId: infoTypes.ExternalDeliveryAdapterId,
			Code: "ExternalDeliveryAdapterId",
			Value: shipping.adapterId
		},
		{
			TypeId: infoTypes.ExternalDeliveryMethodClass,
			Code: "ExternalDeliveryMethodClass",
			Value: d?.class
		},
		{
			TypeId: infoTypes.ExternalDeliverySessionId,
			Code: "ExternalDeliverySessionId",
			Value: shipping.reference
		},
		{
			TypeId: infoTypes.FreightPrice,
			Code: "FreightPrice",
			Value: shipping.total?.excludingVat?.toString()
		},
		{
			TypeId: infoTypes.FreightPriceInclVat,
			Code: "FreightPriceInclVat",
			Value: shipping.total?.includingVat?.toString()
		},
		{
			TypeId: infoTypes.PickupPointId,
			Code: "PickupPointId",
			Value: d?.class === "pickup" ? d?.pickupLocation?.reference : undefined
		},
		{
			TypeId: infoTypes.DeliveryProductReference,
			Code: "DeliveryProductReference",
			Value: d?.product?.reference
		},
		{
			TypeId: infoTypes.ShippingMeta,
			Code: "ShippingMeta",
			Value: extendedShippingMeta
				? JSON.stringify(extendedShippingMeta)
				: undefined
		},
		{
			TypeId: infoTypes.IsbAvailabilityToken,
			Code: "IsbAvailabilityToken",
			Value: shippingMeta?.["isb.availability_token"]
		},
		{
			TypeId: infoTypes.IsbSortCode,
			Code: "IsbSortCode",
			Value: locationMeta?.["isb.sort_code"]
		},
		{
			TypeId: infoTypes.HaulierId,
			Code: "HaulierId",
			Value: d?.carrierProductId
				? haulerIdMap[d.carrierProductId]?.toString()
				: undefined
		},
		{
			TypeId: infoTypes.PaymentInfo,
			Code: "PaymentInfo",
			Value: order.value?.payments?.length
				? JSON.stringify(order.value.payments)
				: undefined
		}
	].filter((item) => Boolean(item.Value) && Boolean(item.TypeId));
	if (shippingInfo.length) {
		const updatedBasket = await updateBasket(norceStore.storeContext, {
			Id: basket.value.Id,
			...(orderId.value ? { OrderReference: orderId.value } : {}),
			Info: shippingInfo
		} as any).catch((err: any) => {
			console.error("[Checkout] updateBasket Info failed:", err);
		});
		if (updatedBasket) {
			cartStore.setBasket(updatedBasket);
		}
	}
};

const onDeliveryUpdated = async () => {
	if (isDeliveryRefreshing.value) {
		pendingDeliveryUpdate = true;
		return;
	}
	isDeliveryRefreshing.value = true;
	try {
		await withIngridSuspended(async () => {
			await loadOrder();
			await updateShippingInfo();
			await emitCheckoutUpdate("delivery");
		});
	} finally {
		isDeliveryRefreshing.value = false;
		if (pendingDeliveryUpdate) {
			pendingDeliveryUpdate = false;
			onDeliveryUpdated();
		}
	}
};

const loadOrder = async (nextOrderId?: string) => {
	const targetOrderId = nextOrderId || orderId.value;
	if (!targetOrderId) {
		order.value = null;
		return null;
	}

	const nextOrder = await norceOrders.getOrderById({
		orderId: targetOrderId,
		localeCode: locale.value
	});
	order.value = nextOrder ?? null;
	return nextOrder ?? null;
};

const placeOrder = async () => {
	try {
		const basketId = basket?.value?.Id;

		// orderId is authoritative — basket.OrderReference may be stale after recovery
		if (orderId.value) {
			await loadOrder(orderId.value);
			return;
		}

		const orderReference = basket?.value?.OrderReference;
		if (orderReference) {
			orderId.value = orderReference;
			await loadOrder(orderReference);
			return;
		}

		// The cart resolves from localStorage after mount, and children mount
		// before the layout — so on the first pass the basket can still be
		// missing. Bail out instead of creating an order without one; the
		// `basket` watcher below re-runs placeOrder as soon as it lands.
		if (!basketId) return;

		if (!basket.value?.PaymentMethodId) {
			await updateBasketPaymentMethod({
				basketId,
				paymentMethodId: NorceCheckoutV2PaymentMethodId,
				localeCode: locale.value
			});
		}

		const orderBody = createOrderBody();
		const { id } = await norceAdapter.createOrder(orderBody);
		orderId.value = id;
		await updateBasket(norceStore.storeContext, {
			Id: basketId,
			OrderReference: id
		});
		await loadOrder(id);
	} catch (error) {
		console.error("Error placing order:", error);
		if (basket.value) {
			isLoading.value = true;
			const orders = await norceOrders.getOrders({
				cart_reference: basket.value.Id?.toString(),
				localeCode: locale.value
			});
			const currentOrder = orders[0];
			if (currentOrder) {
				orderId.value = currentOrder.id;
				order.value = currentOrder;
				await updateBasket(norceStore.storeContext, {
					Id: basket.value.Id,
					OrderReference: currentOrder.id
				});
			} else {
				const orderBody = createOrderBody();
				const { id } = await norceAdapter.createOrder(orderBody);
				orderId.value = id;
				await updateBasket(norceStore.storeContext, {
					Id: basket.value.Id,
					OrderReference: id
				});
				await loadOrder(id);
			}
		}
		isLoading.value = false;
	}
};

const createOrderBody = () => {
	const basketId = basket.value?.Id;
	const currencyCode = norceStore?.getCurrentCurrency?.Code;
	const defaultCulture =
		norceStore.applicationNorce?.Cultures?.Default?.Code ??
		norceStore.cultureCode;
	const defaultCountry =
		norceStore.applicationNorce?.Countries?.Default?.Code ??
		defaultCulture?.split("-")[1];

	if (basketId && currencyCode && defaultCulture && defaultCountry) {
		return {
			cartReference: basketId,
			culture: defaultCulture,
			currency: currencyCode,
			country: defaultCountry,
			localeCode: locale.value
		};
	} else {
		throw new Error("Basket, currency, culture, or country is missing.");
	}
};

onBeforeMount(() => {
	isLoading.value = true;
});

onMounted(async () => {
	await placeOrder();
	if (orderId.value) {
		await emitCheckoutUpdate("cart");
	}
	isLoading.value = false;
});

onMounted(() => {
	if (basket.value?.Items?.length) {
		beginCheckout(basket.value);
		dotdigitalCheckout(basket.value);
	}
});

watch([basket, norceStore], async () => {
	if (
		basket.value &&
		Object.keys(basket.value).length &&
		Object.keys(norceStore).length &&
		!isLoading.value &&
		!isDeliveryRefreshing.value
	) {
		const currentCulture = norceStore.storeContext.cultureCode;
		const currentBasketId = basket.value?.Id;
		const localeChanged = currentCulture !== lastCultureCode;
		const basketSwitched = currentBasketId !== lastBasketId;

		lastCultureCode = currentCulture;
		lastBasketId = currentBasketId;

		if (localeChanged && !basketSwitched) {
			// norceStore locale changed but the basket hasn't switched to the new
			// locale's cart yet. Clear the stale orderId and bail — the basket
			// change will fire this watcher again with the correct basket+locale.
			orderId.value = "";
			order.value = null;
			return;
		}

		if (localeChanged) {
			// Both locale and basket changed in the same tick — reset orderId so
			// the payment adapter starts fresh for the new locale's order.
			orderId.value = "";
			order.value = null;
		}

		if (!localeChanged && !basketSwitched && orderId.value && order.value) {
			await emitCheckoutUpdate("cart");
			return;
		}

		await placeOrder();
		if (orderId.value) {
			await emitCheckoutUpdate("cart");
		}
	}
});
</script>
